'use client';

import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import getCroppedImg from '@/lib/crop-utils';
import { Loader2 } from 'lucide-react';

interface ImageCropperModalProps {
  isOpen: boolean;
  imageSrc: string | null;
  aspectRatio: number;
  onCropComplete: (croppedFile: File) => void | Promise<void>;
  onClose: () => void;
  title?: string;
}

export function ImageCropperModal({
  isOpen,
  imageSrc,
  aspectRatio,
  onCropComplete,
  onClose,
  title,
}: ImageCropperModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeAspect, setActiveAspect] = useState<number | undefined>(aspectRatio);

  // Reset internal state when image changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setActiveAspect(aspectRatio);
    }
  }, [isOpen, imageSrc, aspectRatio]);

  const onCropCompleteHandler = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (croppedFile) {
        await onCropComplete(croppedFile);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const aspectPresets = [
    { label: '4:5', value: 4 / 5, name: 'Producto' },
    { label: '1:1', value: 1, name: 'Cuadrado' },
    { label: '16:9', value: 16 / 9, name: 'Horizontal' },
    { label: '3:4', value: 3 / 4, name: 'Retrato' },
  ];

  return (
    <Dialog open={isOpen && !!imageSrc} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-w-[95vw] rounded-[2rem]">
        <DialogHeader>
          <DialogTitle className="text-center font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
            <span>{title || 'Recortar Imagen'}</span>
          </DialogTitle>
        </DialogHeader>

        {/* ASPECT RATIO SELECTOR */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 text-center">
            Relación de Aspecto
          </p>
          <div className="flex flex-wrap items-center justify-center gap-1.5 px-1">
            {aspectPresets.map((preset) => {
              const isSelected = activeAspect !== undefined && Math.abs(activeAspect - preset.value) < 0.01;
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setActiveAspect(preset.value)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border ${
                    isSelected
                      ? 'bg-zinc-900 text-white border-zinc-900 shadow-sm'
                      : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                  }`}
                >
                  {preset.label} <span className="text-[8px] opacity-70">({preset.name})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative w-full h-[45vh] sm:h-[50vh] min-h-[280px] bg-black/5 rounded-2xl overflow-hidden [&_img]:max-w-none">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={activeAspect}
              onCropChange={setCrop}
              onCropComplete={onCropCompleteHandler}
              onZoomChange={setZoom}
              objectFit="contain"
              restrictPosition={true}
            />
          )}
        </div>

        <div className="py-1 px-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 text-center">
            Ajustar Zoom
          </p>
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={(val) => setZoom(val[0])}
            className="w-full"
          />
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-center">
          <Button variant="outline" onClick={handleClose} disabled={isProcessing} className="flex-1 rounded-xl">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isProcessing} className="flex-1 rounded-xl">
            {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
